import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Skeleton,
} from '@cibli/shared';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import IconButton from '@/features/common/components/IconButton.tsx';
import MainLayout from '@/features/common/components/MainLayout';
import { useI18nDirection } from '@/hooks/useI18nDirection.ts';
import { markdownComponents } from '@/lib/markdown';
import { type CGU as CGUType, getLatestCGU } from '@/services/cgu';

import FormButtons from '../components/FormButtons';
import { useRegisterContext } from '../hooks/useRegisterContext';

interface Props {
  onCancel: () => void;
  onBack: () => void;
  onNext: (data?: { hasAcceptedCGU?: boolean; cguId?: string }) => void | Promise<void>;
}

export default function CGUScreen({ onCancel, onBack, onNext }: Props) {
  const { cguForm } = useRegisterContext();
  const { t } = useTranslation();
  const initialHasAcceptedCGU = cguForm.getValues('hasAcceptedCGU');
  const hasAcceptedCGU = useWatch({
    control: cguForm.control,
    name: 'hasAcceptedCGU',
    defaultValue: initialHasAcceptedCGU,
  });
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(initialHasAcceptedCGU);
  const [cgu, setCgu] = useState<CGUType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const directionDivRef = useI18nDirection<HTMLDivElement>();
  const directionLabelRef = useI18nDirection<HTMLLabelElement>();

  const fetchCGU = useCallback(async () => {
    try {
      setLoading(true);
      const latestCGU = await getLatestCGU();
      setCgu(latestCGU);
      cguForm.setValue('cguId', latestCGU.id);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(t('FormScreen.CGU.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [cguForm, t]);

  useEffect(() => {
    if (!cgu) {
      fetchCGU();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cgu]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  const handleScrollMore = useCallback(() => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollTo({
      top: scrollContainerRef.current.scrollTop + scrollContainerRef.current.clientHeight,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current;

      if (scrollHeight <= clientHeight + 10) {
        setHasScrolledToBottom(true);
      } else {
        handleScroll();
      }
    }
  }, [loading, cgu, handleScroll]);

  return (
    <MainLayout
      padding="p-4 sm:p-8 pb-[120px] sm:pb-8 gap-8"
      className="h-[calc(100dvh_-_72px)] sm:h-[calc(100dvh_-_96px)]"
      onClose={onCancel}
      rightContent={
        onBack ? (
          <IconButton
            className="ml-6"
            icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" />}
            onClick={onBack}
          />
        ) : undefined
      }
    >
      <div
        ref={scrollContainerRef}
        className="bg-white rounded-lg flex-1 w-full overflow-y-auto"
        onScroll={handleScroll}
      >
        <div className="p-6 text-left" ref={directionDivRef}>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center">
              {error}
              <button
                className="block mx-auto mt-4 text-blue-500 underline"
                onClick={() => {
                  setError(null);
                  fetchCGU();
                }}
              >
                {t('common.retry')}
              </button>
            </div>
          ) : cgu ? (
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {cgu.content}
            </Markdown>
          ) : (
            <div className="text-center text-gray-500">{t('FormScreen.CGU.noCGUFound')}</div>
          )}
        </div>
      </div>

      {!loading &&
        !error &&
        cgu &&
        (hasScrolledToBottom ? (
          <div className="relative z-[70] mb-[120px]">
            <Form {...cguForm}>
              <form
                onSubmit={cguForm.handleSubmit(() => {
                  void onNext(cguForm.getValues());
                })}
                className="w-full flex flex-col gap-4"
              >
                <FormField
                  control={cguForm.control}
                  name="hasAcceptedCGU"
                  render={({ field }) => (
                    <FormItem className="flex items-center self-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={checked => field.onChange(checked === true)}
                          id="terms-checkbox"
                          className="size-8 border-2 border-gray-500 ring-1 ring-gray-300"
                          autoFocus
                        />
                      </FormControl>
                      <label htmlFor="terms-checkbox" className="ml-2" ref={directionLabelRef}>
                        {t('FormScreen.CGU.accept')}
                      </label>
                      <FormMessage className="ml-2" />
                    </FormItem>
                  )}
                />

                <FormButtons
                  disableNextButton={!hasAcceptedCGU}
                  showBackButton={!!onBack}
                  onBack={onBack}
                  stepName="cgu"
                />
              </form>
            </Form>
          </div>
        ) : (
          <div className="relative z-[70] flex mt-4 mb-[120px]">
            <Button size="lg" className="w-full" autoFocus onClick={handleScrollMore}>
              ▼ &nbsp;&nbsp;&nbsp;
              {t('FormScreen.CGU.scrollMore')}
              &nbsp;&nbsp;&nbsp; ▼
            </Button>
          </div>
        ))}
    </MainLayout>
  );
}
